import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { useOrganization } from "./useOrganization";
import { LanguageProviderContext } from "@/providers/LanguageProvider";
import clientsService from "@/services/clients/clients";
import { useToast } from "./use-toast";
import { type Client } from "@/types";

export const useClientMutations = () => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const { translator } = useContext(LanguageProviderContext);
  const { toast } = useToast();

  const createClient = useMutation({
    mutationFn: async (client: Omit<Client, 'id' | 'organizationId' | 'createdAt' | 'updatedAt'>) => {
      if (!currentOrganization) throw new Error('No organization selected');
      return await clientsService.createClient(client, currentOrganization.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', currentOrganization?.id] });
      toast({
        title: "Success",
        description: translator("Clients.clientCreatedSuccessfully"),
      });
    },
    onError: (error) => {
      toast({
        title: translator("Global.error") || "Error",
        description: error instanceof Error ? error.message : translator("Clients.clientCreationFailed"),
        variant: "destructive",
      });
    },
  });

  const updateClient = useMutation({
    mutationFn: async (client: Client) => {
      if (!currentOrganization) throw new Error('No organization selected');
      return await clientsService.updateClient(client);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', currentOrganization?.id] });
      toast({
        title: "Success",
        description: translator("Clients.clientUpdatedSuccessfully"),
      });
    },
    onError: (error) => {
      toast({
        title: translator("Global.error") || "Error",
        description: error instanceof Error ? error.message : translator("Clients.clientUpdateFailed"),
        variant: "destructive",
      });
    },
  });

  const deleteClient = useMutation({
    mutationFn: async (clientId: string) => {
      if (!currentOrganization) throw new Error('No organization selected');
      return await clientsService.deleteClient(clientId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', currentOrganization?.id] });
      toast({
        title: "Success",
        description: translator("Clients.clientDeletedSuccessfully"),
      });
    },
    onError: (error) => {
      toast({
        title: translator("Global.error") || "Error",
        description: error instanceof Error ? error.message : translator("Clients.clientDeleteFailed"),
        variant: "destructive",
      });
    },
  });

  const findClientByIdentification = async (identification: string) => {
    if (!currentOrganization) return null;
    return clientsService.getClientByIdentification(currentOrganization.id, identification);
  };

  return {
    createClient: createClient.mutate,
    createClientAsync: createClient.mutateAsync,
    updateClient: updateClient.mutate,
    deleteClient: deleteClient.mutate,
    isCreating: createClient.isPending,
    isUpdating: updateClient.isPending,
    findClientByIdentification,
  };
};
